"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Search, Wrench, MapPin, Phone, Shield, Clock, Truck, Calendar } from "lucide-react";
import { Product as Service } from "../../types/public.types";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import LoginModal from "../LoginModal";

interface ServicesGridProps {
  services: Service[];
  title?: string;
  searchPlaceholder?: string;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  hasServices?: boolean;
  emptyMessage?: string;
  emptySubMessage?: string;
  categorySlug?: string;
  children?: React.ReactNode;
}

export default function ServicesGrid({
  services,
  title,
  searchPlaceholder,
  searchQuery,
  onSearchChange,
  hasServices = true,
  emptyMessage = "No services listed",
  emptySubMessage = "There are no approved services in this category at the moment.",
  categorySlug,
  children,
}: ServicesGridProps) {
  const router = useRouter();
  const { user, activeRole } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedTaluk, setSelectedTaluk] = useState("");

  // Safely extract location fields
  const getLocNames = (srv: any) => {
    const distName = typeof srv.district === 'object' ? srv.district?.name : srv.district;
    return {
      stateName: "Karnataka",
      distName: distName || "",
      talukName: "",
    };
  };

  // Get unique districts from services
  const uniqueDistricts = Array.from(
    new Set(
      services
        .map((s) => getLocNames(s).distName)
        .filter(Boolean)
    )
  ).sort() as string[];

  // Get unique taluks for selected district
  const uniqueTaluks = Array.from(
    new Set(
      services
        .filter((s) => {
          if (!selectedDistrict) return false;
          const { distName } = getLocNames(s);
          return distName.toLowerCase() === selectedDistrict.toLowerCase();
        })
        .map((s) => getLocNames(s).talukName)
        .filter(Boolean)
    )
  ).sort() as string[];

  // Filter services by location
  const locationFilteredServices = services.filter((s) => {
    const { distName, talukName } = getLocNames(s);
    
    if (selectedDistrict && distName.toLowerCase() !== selectedDistrict.toLowerCase()) {
      return false;
    }
    if (selectedTaluk && talukName.toLowerCase() !== selectedTaluk.toLowerCase()) {
      return false;
    }
    return true;
  });

  // Reset selected filters if no longer matching
  React.useEffect(() => {
    if (selectedDistrict && !uniqueDistricts.includes(selectedDistrict)) {
      setSelectedDistrict("");
      setSelectedTaluk("");
    }
  }, [services, selectedDistrict, uniqueDistricts]);

  React.useEffect(() => {
    if (selectedTaluk && !uniqueTaluks.includes(selectedTaluk)) {
      setSelectedTaluk("");
    }
  }, [services, selectedTaluk, uniqueTaluks]);

  const handlePhoneClick = (e: React.MouseEvent) => {
    const isAllowed = user && (activeRole === 'buyer' || activeRole === 'fpo_connect' || user.role === 'buyer' || user.role === 'fpo_connect');
    if (!isAllowed) {
      e.preventDefault();
      e.stopPropagation();
      setIsLoginModalOpen(true);
    } else {
      e.stopPropagation();
    }
  };

  // Helper to extract specific attributes
  const getServiceSpecs = (srv: any) => {
    const priorBookingVal = srv.advanceBooking || "No";
    const transportationVal = srv.transportation || "Not Required";
    return { priorBookingVal, transportationVal };
  };

  return (
    <div className="space-y-6">
      {/* Grid Header */}
      <div className="flex flex-col gap-4 border-b border-slate-200/60 pb-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          {title && (
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-7 bg-emerald-600 rounded-full" />
              <h2 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
                {title}
              </h2>
            </div>
          )}

          {/* Search & Location Filters */}
          {hasServices && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
              {/* Search Bar */}
              {onSearchChange && (
                <div className="relative w-full sm:w-60 shrink-0 shadow-xs rounded-xl">
                  <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder || "Search services..."}
                    value={searchQuery || ""}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-250 rounded-xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-600 text-slate-800 font-semibold transition-all duration-200"
                  />
                </div>
              )}

              {/* District Filter */}
              {uniqueDistricts.length > 0 && (
                <div className="relative w-full sm:w-44 shrink-0 shadow-xs rounded-xl">
                  <select
                    value={selectedDistrict}
                    onChange={(e) => {
                      setSelectedDistrict(e.target.value);
                      setSelectedTaluk("");
                    }}
                    className="w-full pl-3 pr-8 py-2.5 border border-slate-250 rounded-xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-600 text-slate-800 font-semibold transition-all duration-200 appearance-none cursor-pointer"
                  >
                    <option value="">All Districts</option>
                    {uniqueDistricts.map((dist) => (
                      <option key={dist} value={dist}>
                        {dist}
                      </option>
                    ))}
                  </select>
                  <MapPin className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              )}

              {/* Taluk Filter */}
              {selectedDistrict && uniqueTaluks.length > 0 && (
                <div className="relative w-full sm:w-44 shrink-0 shadow-xs rounded-xl">
                  <select
                    value={selectedTaluk}
                    onChange={(e) => setSelectedTaluk(e.target.value)}
                    className="w-full pl-3 pr-8 py-2.5 border border-slate-250 rounded-xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-600 text-slate-800 font-semibold transition-all duration-200 appearance-none cursor-pointer"
                  >
                    <option value="">All Taluks</option>
                    {uniqueTaluks.map((taluk) => (
                      <option key={taluk} value={taluk}>
                        {taluk}
                      </option>
                    ))}
                  </select>
                  <MapPin className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!hasServices ? (
        <div className="bg-white p-20 text-center border border-slate-200 rounded-2xl shadow-xs">
          <Wrench className="w-14 h-14 text-slate-355 mx-auto mb-4" />
          <h5 className="font-bold text-slate-800 text-base">{emptyMessage}</h5>
          <p className="text-sm text-slate-500 mt-1">{emptySubMessage}</p>
        </div>
      ) : locationFilteredServices.length === 0 ? (
        <div className="bg-white p-20 text-center border border-slate-200 rounded-2xl shadow-xs animate-in fade-in duration-300">
          <Search className="w-14 h-14 text-slate-355 mx-auto mb-4" />
          <h5 className="font-bold text-slate-800 text-base">No matching services</h5>
          <p className="text-sm text-slate-500 mt-1">Try resetting or editing your search keyword or location filters.</p>
          {(selectedDistrict || selectedTaluk) && (
            <button
              onClick={() => {
                setSelectedDistrict("");
                setSelectedTaluk("");
              }}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 transition cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {locationFilteredServices.map((srv: any) => {
              const salePrice = srv.applicableFee;

              const currentCategorySlug = srv.category?.slug || categorySlug || "agri-services";
              const currentSubcategorySlug = srv.subcategory?.slug || "general";
              const serviceUrl = `/${currentCategorySlug}/${currentSubcategorySlug}/${srv.slug}`;

              const { priorBookingVal, transportationVal } = getServiceSpecs(srv);
              const { distName } = getLocNames(srv);
              const coverageLocation = distName ? `${distName}, Karnataka` : "";

              return (
                <div
                  key={srv._id}
                  onClick={() => router.push(serviceUrl)}
                  className="group bg-white rounded-lg border border-slate-200 hover:border-emerald-500 transition-all duration-300 hover:shadow-lg flex flex-col justify-between overflow-hidden shadow-xs cursor-pointer"
                >
                  {/* Service Cover Frame */}
                  <div className="relative w-full h-[150px] bg-slate-100 flex items-center justify-center overflow-hidden border-b border-slate-100">
                    {srv.imageUrl ? (
                      <Image
                        src={srv.imageUrl}
                        alt={srv.title}
                        fill
                        sizes="(max-width: 768px) 100vw, 25vw"
                        className="object-cover group-hover:scale-102 transition-transform duration-500"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-1.5 text-slate-350">
                        <Wrench className="w-12 h-12" />
                        <span className="text-[9px] font-bold tracking-widest uppercase">Service</span>
                      </div>
                    )}
                    {srv.vendorDetails?.isVerified !== false && (
                      <div className="absolute top-3 left-3 bg-emerald-700 text-white text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded-sm shadow-xs flex items-center gap-0.5 z-10">
                        <Shield className="w-3 h-3" /> Verified Provider
                      </div>
                    )}
                  </div>

                  {/* Body Content */}
                  <div className="p-4 flex-1 flex flex-col justify-between space-y-4">
                    <div className="space-y-2">
                      <h4 className="font-extrabold text-slate-800 text-sm line-clamp-2 leading-tight group-hover:text-emerald-800 transition-colors text-left" title={srv.title}>
                        {srv.title}
                      </h4>
                      <p className="text-[11px] text-slate-500 line-clamp-2 text-left font-medium">
                        {srv.description || srv.tagline}
                      </p>
                    </div>

                    {/* Specifications badges */}
                    <div className="pt-2 border-t border-slate-100 grid grid-cols-2 gap-2 text-[10px] font-bold text-slate-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <span className="truncate" title={`Booking: ${priorBookingVal}`}>{priorBookingVal}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Truck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <span className="truncate" title={`Transport: ${transportationVal}`}>{transportationVal}</span>
                      </div>
                    </div>

                    {/* Bottom Pricing & Call actions */}
                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                      <div className="text-left">
                        <span className="block text-[8px] font-black text-slate-400 uppercase tracking-wider">Charges</span>
                        <span className="font-black text-slate-800 text-xs">
                          {salePrice !== null ? `₹${salePrice} / ${srv.feeUnit || "Acre"}` : "On Request"}
                        </span>
                      </div>

                      {/* Action Links */}
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        {srv.mobile ? (
                          <a
                            href={`tel:${srv.mobile}`}
                            onClick={handlePhoneClick}
                            className="bg-[#0f9648] hover:bg-green-700 text-white p-2 rounded-lg transition"
                            title="Call Provider"
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </a>
                        ) : srv.vendorDetails?.phoneNumber ? (
                          <a
                            href={`tel:${srv.vendorDetails.phoneNumber}`}
                            onClick={handlePhoneClick}
                            className="bg-[#0f9648] hover:bg-green-700 text-white p-2 rounded-lg transition"
                            title="Call Provider"
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </a>
                        ) : (
                          <button
                            onClick={() => router.push(serviceUrl)}
                            className="bg-[#0f9648] hover:bg-green-700 text-white p-2 rounded-lg transition"
                          >
                            <Calendar className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Location strip */}
                  {coverageLocation && (
                    <div className="bg-slate-50 border-t border-slate-100 px-4 py-1.5 flex items-center gap-1 text-[10px] text-slate-500 font-bold justify-start">
                      <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                      <span className="truncate">{coverageLocation}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
        </>
      )}
    </div>
  );
}
